import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Download, Mail, MessageCircle, CreditCard, Image, Plus, Trash2,
  CheckCircle, Clock, AlertCircle, RefreshCw, Upload, FileText
} from 'lucide-react';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n || 0);

const PAYMENT_METHODS = ['NEFT', 'RTGS', 'IMPS', 'UPI', 'Cash', 'Cheque', 'DD'];

const PAYMENT_STATUS_STYLE = {
  Paid:    'bg-green-900/40 text-green-300 border-green-700',
  Partial: 'bg-amber-900/40 text-amber-300 border-amber-700',
  Unpaid:  'bg-red-900/40 text-red-300 border-red-700',
};

export default function InvoiceModal({ order, onClose, onUpdate }) {
  const [activeTab, setActiveTab] = useState('invoice');
  const [orderData, setOrderData] = useState(order);
  const [attachments, setAttachments] = useState([]);
  const [emailForm, setEmailForm] = useState({ to_email: order.client_email || '', sending: false, sent: false, error: '' });
  const [payForm, setPayForm] = useState({ amount: '', date: new Date().toISOString().slice(0, 10), method: 'NEFT', note: '' });
  const [payLoading, setPayLoading] = useState(false);
  const [attachLoading, setAttachLoading] = useState(false);
  const [uploadType, setUploadType] = useState('challan');  // challan | coa | other
  const [confirmAction, setConfirmAction] = useState({ open: false, title: '', message: '', onConfirm: null });
  // Twilio WhatsApp auto-send: detect if backend has TWILIO env vars set so we
  // can offer a "send now (no manual click)" flow alongside the wa.me link.
  const [twilio, setTwilio] = useState({ configured: false, sender: null, is_sandbox: false });
  const [twilioForm, setTwilioForm] = useState({ phone: order.client_phone || '', message: '', sending: false });
  const fileRef = useRef();

  const isGst = orderData.gst_enabled !== false;
  const billingEntity = isGst ? 'Tirth Industries' : 'Tirth Enterprises';
  // Support multi-line items[] or legacy single-item
  const subtotal = orderData.subtotal
    || (orderData.items?.length > 0
      ? orderData.items.reduce((s, it) => s + (parseFloat(it.amount) || parseFloat(it.qty || 0) * parseFloat(it.rate || 0)), 0)
      : (parseFloat(orderData.quantity) || 0) * (parseFloat(orderData.unit_price) || 0));
  const gstRate = isGst ? (orderData.gst_rate || 5) : 0;
  const gstAmt = orderData.gst_amount || (subtotal * gstRate / 100);
  const transportExp = parseFloat(orderData.transport_exp) || 0;
  const total = orderData.total_amount || (subtotal + gstAmt + transportExp);
  const paidAmt = orderData.paid_amount || 0;
  const outstanding = Math.max(0, total - paidAmt);
  const payStatus = orderData.payment_status || 'Unpaid';

  useEffect(() => { fetchAttachments(); }, []);

  // One-shot probe: does the backend have Twilio WhatsApp configured?
  useEffect(() => {
    axios.get(`${API}/invoices/share/twilio-status`, { withCredentials: true })
      .then(r => setTwilio(r.data || { configured: false }))
      .catch(() => {});
  }, []);

  const fetchAttachments = async () => {
    try {
      const res = await axios.get(`${API}/invoices/${orderData.id}/attachments`, { withCredentials: true });
      setAttachments(res.data);
    } catch (e) { console.error(e); }
  };

  const pdfUrl = `${API}/invoices/${orderData.id}/pdf`;

  const handleDownload = async () => {
    try {
      const res = await fetch(pdfUrl, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `Invoice_${orderData.invoice_number || orderData.id}.pdf`;
      a.rel = 'noopener';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); window.URL.revokeObjectURL(blobUrl); }, 300);
      toast.success('Download started');
    } catch (e) { toast.error('Download failed — ' + (e.message || 'retry')); }
  };

  const handlePrint = () => {
    // Open PDF in new tab → browser's native viewer → Ctrl+P prints Excel layout
    const w = window.open(pdfUrl, '_blank', 'noopener');
    if (!w) toast.info('Allow pop-ups and retry');
  };

  const handleEmail = async () => {
    if (!emailForm.to_email) return;
    setEmailForm(f => ({ ...f, sending: true, error: '' }));
    try {
      await axios.post(`${API}/invoices/${orderData.id}/email`, { to_email: emailForm.to_email }, { withCredentials: true });
      setEmailForm(f => ({ ...f, sending: false, sent: true }));
      setTimeout(() => setEmailForm(f => ({ ...f, sent: false })), 4000);
      // refresh order to get invoice number
      await refreshOrder();
    } catch (e) {
      setEmailForm(f => ({ ...f, sending: false, error: e.response?.data?.detail || 'Email failed' }));
    }
  };

  const handleWhatsApp = async () => {
    try {
      const res = await axios.get(`${API}/invoices/${orderData.id}/summary`, { withCredentials: true });
      const d = res.data;
      // Generate a public PDF download link so the recipient can tap it inside
      // WhatsApp and download the invoice without needing to log in.
      let pdfUrl = '';
      try {
        const lr = await axios.get(`${API}/invoices/${orderData.id}/public-pdf-link`, { withCredentials: true });
        pdfUrl = lr.data?.url || '';
      } catch { /* non-fatal */ }
      const totalLine = `Total: *Rs. ${Number(d.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}*`;
      const linkLine = pdfUrl ? `\n\nDownload PDF: ${pdfUrl}\n_(link valid for 7 days)_` : `\n\n_PDF will be shared separately._`;
      const msg = `*Invoice ${d.invoice_number}*\nFrom: *${d.billing_entity}*\n\nClient: ${d.client}\nProduct: ${d.product_name}\nQty: ${d.quantity}\n${totalLine}\nDate: ${new Date().toLocaleDateString('en-IN')}${linkLine}\n\nContact: +91 9987951354`;
      // Auto-target client phone when we have one — saves the "pick contact" step in WhatsApp.
      let phone = (orderData.client_phone || d.client_phone || '').toString().replace(/\D/g, '');
      if (!phone) {
        // Look up client record as fallback
        try {
          const cs = await axios.get(`${API}/clients`, { withCredentials: true });
          const match = (cs.data || []).find(c => (c.name || '').toLowerCase() === (d.client || '').toLowerCase());
          if (match) phone = (match.phone || match.whatsapp || '').toString().replace(/\D/g, '');
        } catch { /* ignore */ }
      }
      if (phone && phone.length === 10) phone = '91' + phone;     // assume India if 10-digit
      const target = phone ? `https://wa.me/${phone}` : 'https://wa.me/';
      window.open(`${target}?text=${encodeURIComponent(msg)}`, '_blank');
      setOrderData(prev => ({ ...prev, invoice_number: d.invoice_number }));
    } catch (e) { toast.error('Error generating WhatsApp link'); }
  };

  const handleTwilioSend = async () => {
    const phone = (twilioForm.phone || '').replace(/\D/g, '');
    if (!phone || phone.length < 10) { toast.error('Enter a 10-digit (or E.164) phone number'); return; }
    setTwilioForm(f => ({ ...f, sending: true }));
    try {
      const res = await axios.post(
        `${API}/invoices/${orderData.id}/whatsapp-twilio`,
        { to_phone: phone, message: twilioForm.message || '' },
        { withCredentials: true },
      );
      toast.success(`WhatsApp sent · SID ${String(res.data?.sid || '').slice(0, 10)}…`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Twilio send failed');
    } finally {
      setTwilioForm(f => ({ ...f, sending: false }));
    }
  };

  const refreshOrder = async () => {
    try {
      // Re-fetch the invoice summary to get invoice_number etc.
      const res = await axios.get(`${API}/invoices/${orderData.id}/summary`, { withCredentials: true });
      setOrderData(prev => ({ ...prev, invoice_number: res.data.invoice_number, payment_status: res.data.payment_status, paid_amount: res.data.paid_amount }));
    } catch (e) { /* silent */ }
  };

  const addPayment = async () => {
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) return;
    setPayLoading(true);
    try {
      const res = await axios.post(`${API}/invoices/${orderData.id}/payment`, payForm, { withCredentials: true });
      const updated = {
        ...orderData,
        payments: [...(orderData.payments || []), { ...payForm, id: res.data.payment?.id }],
        paid_amount: res.data.paid_total,
        payment_status: res.data.payment_status,
      };
      setOrderData(updated);
      if (onUpdate) onUpdate(updated);
      setPayForm(f => ({ ...f, amount: '', note: '' }));
      toast.success('Payment recorded!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error adding payment'); }
    finally { setPayLoading(false); }
  };

  const removePayment = async (payId) => {
    setConfirmAction({
      open: true,
      title: 'Remove Payment?',
      message: 'This will remove the recorded payment. The invoice status will be recalculated.',
      onConfirm: async () => {
        setConfirmAction({ open: false, title: '', message: '', onConfirm: null });
        try {
          await axios.delete(`${API}/invoices/${orderData.id}/payment/${payId}`, { withCredentials: true });
          const updated = {
            ...orderData,
            payments: (orderData.payments || []).filter(p => p.id !== payId),
          };
          const paid = updated.payments.reduce((s, p) => s + Number(p.amount), 0);
          updated.paid_amount = paid;
          updated.payment_status = paid >= total ? 'Paid' : paid > 0 ? 'Partial' : 'Unpaid';
          setOrderData(updated);
          if (onUpdate) onUpdate(updated);
        } catch (e) { toast.error('Error removing payment'); }
      }
    });
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (attachments.length + files.length > 5) { toast.error('Max 5 attachments allowed'); return; }
    setAttachLoading(true);
    try {
      const fileObjs = await Promise.all(files.map(file => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve({
          filename: file.name,
          data: reader.result,
          mime_type: file.type || '',
          attachment_type: uploadType,
        });
        reader.readAsDataURL(file);
      })));
      await axios.post(`${API}/invoices/${orderData.id}/attachments`, { files: fileObjs }, { withCredentials: true });
      await fetchAttachments();
      toast.success(`Uploaded ${files.length} ${uploadType === 'coa' ? 'COA' : uploadType === 'challan' ? 'Challan' : 'attachment'}${files.length > 1 ? 's' : ''}`);
      // Clear input so the same file can be re-selected
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setAttachLoading(false); }
  };

  const deleteAttachment = async (attId) => {
    setConfirmAction({
      open: true,
      title: 'Delete Attachment?',
      message: 'This attachment will be permanently removed.',
      onConfirm: async () => {
        setConfirmAction({ open: false, title: '', message: '', onConfirm: null });
        await axios.delete(`${API}/invoices/${orderData.id}/attachments/${attId}`, { withCredentials: true });
        setAttachments(prev => prev.filter(a => a.id !== attId));
      }
    });
  };

  const tabs = [
    { id: 'invoice',     label: 'Invoice',     icon: FileText },
    { id: 'payments',    label: 'Payments',    icon: CreditCard },
    { id: 'attachments', label: 'Attachments', icon: Image },
  ];

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="invoice-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div>
            <h3 className="font-heading font-semibold text-zinc-50 text-base">
              {orderData.invoice_number || `Order #${orderData.id?.slice(-6)}`}
            </h3>
            <p className="text-zinc-500 text-xs mt-0.5">{billingEntity} · {orderData.client}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${PAYMENT_STATUS_STYLE[payStatus] || PAYMENT_STATUS_STYLE.Unpaid}`} data-testid="payment-status-badge">
              {payStatus}
            </span>
            <button onClick={onClose}><X size={18} className="text-zinc-500 hover:text-zinc-200" /></button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-3 border-b border-zinc-800 pb-0">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setActiveTab(id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg transition-colors ${activeTab === id ? 'bg-zinc-800 text-amber-400 border border-b-0 border-zinc-700' : 'text-zinc-500 hover:text-zinc-300'}`}
              data-testid={`invoice-tab-${id}`}
            >
              <Icon size={12} />{label}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto flex-1 p-5">
          {/* ── INVOICE TAB ───────────────────────────────────────────────── */}
          {activeTab === 'invoice' && (
            <div className="space-y-4">
              {/* Editable Invoice Form */}
              <div className="space-y-3" data-testid="invoice-form-view">
                {/* Entity & Client Header */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-zinc-800 rounded-xl p-3">
                    <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Billing Entity</div>
                    <div className={`text-sm font-semibold ${isGst ? 'text-green-400' : 'text-amber-400'}`}>{billingEntity}</div>
                    <div className="text-zinc-600 text-[10px] mt-0.5">{isGst ? 'Tax Invoice · GST' : 'Bill of Supply · Non-GST'}</div>
                  </div>
                  <div className="bg-zinc-800 rounded-xl p-3">
                    <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Invoice</div>
                    <div className="text-amber-400 text-sm font-mono font-bold">{orderData.invoice_number || orderData.voucher_no || '—'}</div>
                    <div className="text-zinc-600 text-[10px] mt-0.5">{orderData.invoice_date || orderData.created_at?.slice(0, 10)}</div>
                  </div>
                </div>

                {/* Buyer Info */}
                <div className="bg-zinc-800 rounded-xl p-3 space-y-1">
                  <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Buyer</div>
                  <div className="text-zinc-100 text-sm font-semibold">{orderData.client}</div>
                  {orderData.client_address && <div className="text-zinc-400 text-xs">{orderData.client_address}</div>}
                  <div className="flex gap-4 text-[10px] text-zinc-500">
                    {orderData.client_phone && <span>Mob: {orderData.client_phone}</span>}
                    {orderData.client_gstin && <span>GSTIN: {orderData.client_gstin}</span>}
                    {orderData.client_state && <span>State: {orderData.client_state}</span>}
                  </div>
                </div>

                {/* Dispatch/Reference Details */}
                {(orderData.dispatch_through || orderData.destination || orderData.lr_no || orderData.buyer_ref || orderData.payment_terms) && (
                  <div className="bg-zinc-800/60 rounded-xl p-3">
                    <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1.5">Dispatch & Reference</div>
                    <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-[10px]">
                      {[
                        ['Dispatch Through', orderData.dispatch_through],
                        ['Destination', orderData.destination],
                        ['Vehicle No.', orderData.vehicle_no],
                        ['LR / Delivery Note', orderData.lr_no],
                        ['Buyer Ref', orderData.buyer_ref],
                        ['Payment Terms', orderData.payment_terms],
                        ...(isGst ? [['e-Way Bill', orderData.eway_bill_no]] : [
                          ['Supplier Ref', orderData.supplier_ref],
                          ['Terms of Delivery', orderData.terms_of_delivery],
                        ]),
                      ].filter(([, v]) => v).map(([label, value]) => (
                        <div key={label}><span className="text-zinc-600">{label}:</span> <span className="text-zinc-300">{value}</span></div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Line Items Table */}
                <div className="bg-zinc-800 rounded-xl overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-zinc-700">
                        <th className="text-left text-zinc-500 text-[10px] px-3 py-2">#</th>
                        <th className="text-left text-zinc-500 text-[10px] px-3 py-2">Description</th>
                        <th className="text-right text-zinc-500 text-[10px] px-3 py-2">Qty</th>
                        <th className="text-center text-zinc-500 text-[10px] px-3 py-2">Unit</th>
                        <th className="text-right text-zinc-500 text-[10px] px-3 py-2">Rate</th>
                        <th className="text-right text-zinc-500 text-[10px] px-3 py-2">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(orderData.items?.length > 0 ? orderData.items : [{ product_name: orderData.product_name, qty: orderData.quantity, unit: orderData.unit || 'KG', rate: orderData.unit_price, amount: subtotal }]).map((it, i) => (
                        <tr key={i} className="border-b border-zinc-800/50">
                          <td className="px-3 py-2 text-zinc-600">{i + 1}</td>
                          <td className="px-3 py-2 text-zinc-200">
                            {it.product_name || it.name || '—'}
                            {it.item_type === 'raw' && <span className="ml-2 text-[9px] uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-700/30 px-1.5 py-0.5 rounded">RM</span>}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-zinc-300">{it.qty || it.quantity || '—'}</td>
                          <td className="px-3 py-2 text-center text-zinc-500">{it.unit || 'KG'}</td>
                          <td className="px-3 py-2 text-right font-mono text-zinc-300">{fmt(it.rate || it.unit_price || 0)}</td>
                          <td className="px-3 py-2 text-right font-mono text-amber-400">{fmt(it.amount || (parseFloat(it.qty || 0) * parseFloat(it.rate || 0)))}</td>
                        </tr>
                      ))}
                      {/* Extra charges rendered as their own rows (transport, packing, etc.) */}
                      {(orderData.extra_charges || []).filter(c => parseFloat(c.amount) > 0).map((c, i) => (
                        <tr key={`extra-${i}`} className="border-b border-zinc-800/30 bg-amber-500/5">
                          <td className="px-3 py-1.5 text-zinc-600">{(orderData.items?.length || 1) + i + 1}</td>
                          <td className="px-3 py-1.5 text-zinc-300 text-xs italic">{c.label}</td>
                          <td colSpan={3} className="px-3 py-1.5 text-zinc-600 text-right text-[10px]">extra charge</td>
                          <td className="px-3 py-1.5 text-right font-mono text-amber-300">{fmt(c.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="bg-zinc-800 rounded-xl p-3 space-y-1.5 text-xs">
                  <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span className="font-mono">{fmt(subtotal)}</span></div>
                  {isGst && <div className="flex justify-between text-zinc-500"><span>CGST ({gstRate / 2}%)</span><span className="font-mono">{fmt(gstAmt / 2)}</span></div>}
                  {isGst && <div className="flex justify-between text-zinc-500"><span>SGST ({gstRate / 2}%)</span><span className="font-mono">{fmt(gstAmt / 2)}</span></div>}
                  {transportExp > 0 && <div className="flex justify-between text-zinc-500"><span>Transport</span><span className="font-mono">{fmt(transportExp)}</span></div>}
                  <div className="flex justify-between border-t border-zinc-700 pt-1.5 font-semibold">
                    <span className="text-zinc-200">Total</span><span className="text-amber-400 font-mono">{fmt(total)}</span>
                  </div>
                  <div className="flex justify-between text-zinc-500"><span>Paid</span><span className="text-green-400 font-mono">{fmt(paidAmt)}</span></div>
                  {outstanding > 0 && <div className="flex justify-between font-semibold"><span className="text-red-400">Outstanding</span><span className="text-red-400 font-mono">{fmt(outstanding)}</span></div>}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-4 gap-2">
                <button data-testid="download-pdf-btn" onClick={handleDownload} className="flex flex-col items-center gap-1.5 p-3 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-xl transition-colors group">
                  <Download size={18} className="text-amber-400 group-hover:scale-110 transition-transform" />
                  <span className="text-zinc-300 text-xs">Download</span>
                </button>
                <button data-testid="print-pdf-btn" onClick={handlePrint} className="flex flex-col items-center gap-1.5 p-3 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-xl transition-colors group">
                  <Download size={18} className="text-green-400 group-hover:scale-110 transition-transform" />
                  <span className="text-zinc-300 text-xs">Print</span>
                </button>
                <button data-testid="whatsapp-share-btn" onClick={handleWhatsApp} className="flex flex-col items-center gap-1.5 p-3 bg-zinc-800 hover:bg-green-900/30 border border-zinc-700 hover:border-green-700/50 rounded-xl transition-colors group">
                  <MessageCircle size={18} className="text-green-400 group-hover:scale-110 transition-transform" />
                  <span className="text-zinc-300 text-xs">WhatsApp</span>
                </button>
                <button data-testid="email-tab-btn" onClick={() => setActiveTab('email-send')} className="flex flex-col items-center gap-1.5 p-3 bg-zinc-800 hover:bg-blue-900/20 border border-zinc-700 hover:border-blue-700/50 rounded-xl transition-colors group">
                  <Mail size={18} className="text-blue-400 group-hover:scale-110 transition-transform" />
                  <span className="text-zinc-300 text-xs">Email</span>
                </button>
              </div>

              {/* Email Send Form (inline) */}
              <div className="bg-zinc-800/60 rounded-xl p-4 border border-zinc-700" data-testid="email-send-section">
                <p className="text-zinc-400 text-xs mb-2 font-medium">Send via Email</p>
                <div className="flex gap-2">
                  <input data-testid="email-input" type="email" value={emailForm.to_email}
                    onChange={e => setEmailForm(f => ({ ...f, to_email: e.target.value }))}
                    placeholder="client@example.com"
                    className="flex-1 bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  <button data-testid="send-email-btn" onClick={handleEmail}
                    disabled={emailForm.sending || !emailForm.to_email}
                    className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-xl disabled:opacity-50 transition-colors">
                    {emailForm.sending ? <RefreshCw size={12} className="animate-spin" /> : <Mail size={12} />}
                    {emailForm.sending ? 'Sending...' : 'Send'}
                  </button>
                </div>
                {emailForm.sent && <p className="text-green-400 text-xs mt-1.5 flex items-center gap-1"><CheckCircle size={11} /> Invoice sent successfully!</p>}
                {emailForm.error && <p className="text-red-400 text-xs mt-1.5">{emailForm.error}</p>}
              </div>

              {/* Twilio WhatsApp auto-send — only shown when backend has Twilio creds.
                  Unlike the wa.me link, this delivers the PDF natively without user
                  picking a contact in WhatsApp. Sandbox numbers require recipient to
                  "join <code>" once before they receive messages. */}
              {twilio.configured && (
                <div className="bg-zinc-800/60 rounded-xl p-4 border border-green-700/40" data-testid="twilio-send-section">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-zinc-400 text-xs font-medium">WhatsApp via Twilio (auto-send + PDF)</p>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded ${twilio.is_sandbox ? 'bg-amber-900/40 text-amber-300 border border-amber-700' : 'bg-green-900/40 text-green-300 border border-green-700'}`}>
                      {twilio.is_sandbox ? 'Sandbox' : 'Production'}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                    <input data-testid="twilio-phone" value={twilioForm.phone}
                      onChange={e => setTwilioForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="9876543210 or +9198…"
                      className="md:col-span-1 bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                    <input data-testid="twilio-message" value={twilioForm.message}
                      onChange={e => setTwilioForm(f => ({ ...f, message: e.target.value }))}
                      placeholder="Custom message (leave blank for default)"
                      className="md:col-span-2 bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                  </div>
                  <button data-testid="twilio-send-btn" onClick={handleTwilioSend}
                    disabled={twilioForm.sending || !twilioForm.phone}
                    className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-500 text-white text-xs font-semibold rounded-xl disabled:opacity-50 transition-colors">
                    {twilioForm.sending ? <RefreshCw size={12} className="animate-spin" /> : <MessageCircle size={12} />}
                    {twilioForm.sending ? 'Sending…' : 'Send via Twilio'}
                  </button>
                  {twilio.is_sandbox && (
                    <p className="text-amber-300 text-[10px] mt-1.5">
                      Sandbox mode: recipient must first send <span className="font-mono">join &lt;your-code&gt;</span> to {twilio.sender} once.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── PAYMENTS TAB ──────────────────────────────────────────────── */}
          {activeTab === 'payments' && (
            <div className="space-y-4">
              {/* Outstanding Summary */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'Invoice Total', value: fmt(total), color: 'text-zinc-200' },
                  { label: 'Paid', value: fmt(paidAmt), color: 'text-green-400' },
                  { label: 'Outstanding', value: fmt(outstanding), color: outstanding > 0 ? 'text-red-400' : 'text-green-400' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-zinc-800 rounded-xl p-3">
                    <div className="text-zinc-500 text-xs">{label}</div>
                    <div className={`font-mono font-bold text-sm mt-0.5 ${color}`}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Add Payment Form */}
              <div className="bg-zinc-800 rounded-xl p-4 space-y-3" data-testid="add-payment-form">
                <p className="text-zinc-300 text-xs font-semibold">Record Payment</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-zinc-500 text-xs mb-1 block">Amount (Rs.) *</label>
                    <input
                      data-testid="payment-amount-input"
                      type="number"
                      value={payForm.amount}
                      onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))}
                      placeholder="0.00"
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-500 text-xs mb-1 block">Date *</label>
                    <input
                      type="date"
                      value={payForm.date}
                      onChange={e => setPayForm(f => ({ ...f, date: e.target.value }))}
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-500 text-xs mb-1 block">Method</label>
                    <select
                      value={payForm.method}
                      onChange={e => setPayForm(f => ({ ...f, method: e.target.value }))}
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500"
                    >
                      {PAYMENT_METHODS.map(m => <option key={m} value={m} className="bg-zinc-900">{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-zinc-500 text-xs mb-1 block">Note / Ref</label>
                    <input
                      data-testid="payment-note-input"
                      value={payForm.note}
                      onChange={e => setPayForm(f => ({ ...f, note: e.target.value }))}
                      placeholder="UTR / Cheque no."
                      className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500"
                    />
                  </div>
                </div>
                <button
                  data-testid="add-payment-btn"
                  onClick={addPayment}
                  disabled={payLoading || !payForm.amount}
                  className="w-full py-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm rounded-xl disabled:opacity-50 transition-colors"
                >
                  {payLoading ? 'Saving...' : `Record ${payForm.amount ? fmt(parseFloat(payForm.amount)) : ''} Payment`}
                </button>
              </div>

              {/* Payment History */}
              {(orderData.payments || []).length > 0 && (
                <div>
                  <p className="text-zinc-500 text-xs mb-2 font-medium">Payment History</p>
                  <div className="space-y-2" data-testid="payment-history">
                    {(orderData.payments || []).map((p, i) => (
                      <div key={p.id || i} className="flex items-center justify-between bg-zinc-800 rounded-xl px-4 py-2.5">
                        <div>
                          <span className="text-green-400 font-mono font-semibold text-sm">{fmt(p.amount)}</span>
                          <span className="text-zinc-500 text-xs ml-2">{p.method}</span>
                          {p.note && <span className="text-zinc-600 text-xs ml-2">· {p.note}</span>}
                          <div className="text-zinc-600 text-xs mt-0.5">{p.date} · {p.recorded_by}</div>
                        </div>
                        <button onClick={() => removePayment(p.id)} className="text-zinc-700 hover:text-red-400 transition-colors ml-2">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── ATTACHMENTS TAB ───────────────────────────────────────────── */}
          {activeTab === 'attachments' && (
            <div className="space-y-4">
              <div>
                <p className="text-zinc-400 text-sm mb-2">Challans, COA reports & other documents ({attachments.length}/5)</p>
                <p className="text-zinc-600 text-[11px] mb-3">Uploaded PDFs/images are merged into the invoice PDF download.</p>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-zinc-500 text-xs">Type:</span>
                  {[
                    { v: 'challan', label: 'Challan' },
                    { v: 'coa', label: 'COA' },
                    { v: 'other', label: 'Other' },
                  ].map(o => (
                    <button
                      key={o.v}
                      data-testid={`upload-type-${o.v}`}
                      onClick={() => setUploadType(o.v)}
                      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                        uploadType === o.v
                          ? 'bg-amber-500 border-amber-400 text-black font-semibold'
                          : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-zinc-200'
                      }`}
                    >{o.label}</button>
                  ))}
                  <div className="flex-1" />
                  {attachments.length < 5 && (
                    <button
                      data-testid="upload-attachment-btn"
                      onClick={() => fileRef.current?.click()}
                      disabled={attachLoading}
                      className="flex items-center gap-1.5 text-xs bg-amber-500 hover:bg-amber-400 text-black font-semibold px-3 py-1.5 rounded-xl transition-colors disabled:opacity-50"
                    >
                      {attachLoading ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />}
                      Upload {uploadType === 'coa' ? 'COA' : uploadType === 'challan' ? 'Challan' : 'File'}
                    </button>
                  )}
                </div>
                <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" onChange={handleFileUpload} className="hidden" />
              </div>

              {attachments.length === 0 ? (
                <div
                  className="border-2 border-dashed border-zinc-700 rounded-xl p-8 text-center cursor-pointer hover:border-amber-500/50 transition-colors"
                  onClick={() => fileRef.current?.click()}
                  data-testid="empty-attachments-drop"
                >
                  <Image size={28} className="text-zinc-700 mx-auto mb-2" />
                  <p className="text-zinc-500 text-sm">Click to upload Challan / COA / other documents</p>
                  <p className="text-zinc-700 text-xs mt-1">Images (JPG/PNG) or PDF — max 5 files. Merged into invoice PDF.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {attachments.map(att => {
                    const isPdf = (att.mime_type || '').includes('pdf') || (att.filename || '').toLowerCase().endsWith('.pdf');
                    const typeLabel = att.attachment_type === 'coa' ? 'COA' : att.attachment_type === 'other' ? 'Other' : 'Challan';
                    const typeColor = att.attachment_type === 'coa' ? 'bg-blue-900/50 text-blue-300 border-blue-700' : att.attachment_type === 'other' ? 'bg-zinc-700 text-zinc-300 border-zinc-600' : 'bg-amber-900/50 text-amber-300 border-amber-700';
                    return (
                      <div key={att.id} className="relative group rounded-xl overflow-hidden border border-zinc-700" data-testid={`attachment-${att.id}`}>
                        <div className="absolute top-2 left-2 z-10">
                          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${typeColor}`}>{typeLabel}</span>
                        </div>
                        {isPdf ? (
                          <div className="w-full h-32 bg-zinc-900 flex flex-col items-center justify-center">
                            <FileText size={36} className="text-red-400" />
                            <span className="text-zinc-500 text-[10px] mt-1">PDF document</span>
                          </div>
                        ) : (
                          <img
                            src={att.data}
                            alt={att.filename}
                            className="w-full h-32 object-cover"
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <div className="p-2 bg-zinc-800">
                          <p className="text-zinc-400 text-xs truncate" title={att.filename}>{att.filename}</p>
                          <p className="text-zinc-600 text-xs">{att.uploaded_at?.slice(0, 10)}</p>
                        </div>
                        <button
                          onClick={() => deleteAttachment(att.id)}
                          data-testid={`delete-attachment-${att.id}`}
                          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-600/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={10} />
                        </button>
                      </div>
                    );
                  })}
                  {attachments.length < 5 && (
                    <div
                      onClick={() => fileRef.current?.click()}
                      className="border-2 border-dashed border-zinc-700 rounded-xl h-32 flex flex-col items-center justify-center cursor-pointer hover:border-amber-500/50 transition-colors"
                    >
                      <Plus size={20} className="text-zinc-600" />
                      <span className="text-zinc-600 text-xs mt-1">Add more</span>
                    </div>
                  )}
                </div>
              )}

              {/* WhatsApp share */}
              {attachments.length > 0 && (
                <button
                  onClick={handleWhatsApp}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-green-700/20 hover:bg-green-700/30 border border-green-700/50 rounded-xl text-green-300 text-sm font-medium transition-colors"
                  data-testid="whatsapp-challan-btn"
                >
                  <MessageCircle size={14} />
                  Share via WhatsApp (with Invoice Details)
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <ConfirmDeleteModal open={confirmAction.open} onClose={() => setConfirmAction({ open: false, title: '', message: '', onConfirm: null })} onConfirm={confirmAction.onConfirm || (() => {})} title={confirmAction.title} message={confirmAction.message} />
    </div>
  );
}
